import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { FaShareAlt } from "react-icons/fa";
import ShareModal from "../../EventCard/LatestCard/ShareModal";
import {
  TbCalendarEvent,
  TbFlame,
  TbHeart,
  TbMapPin,
  TbSearch,
  TbTrendingUp,
  // TbTrophy,
} from "react-icons/tb";
// import MarketplaceNewUIArtistSpotlight from "./MarketplaceNewUIArtistSpotlight";
import {
  mapCommunityWrapperToCard,
  mapFeaturedWrapperToHotCard,
  mapFeaturedWrapperToTrendingCard,
  selectCommunityItems,
  selectLatestFeaturedForHot,
  selectTrendingFeatured,
} from "./marketplaceHomeFeaturedFromApi";
import {
  applyListingPrices,
  useDiscoverListingPrices,
} from "./marketplaceDiscoverListingPrices";

function DiscoverEmpty({ label }) {
  return (
    <p className="col-span-full text-center text-white/40 text-sm py-10">
      No {label} available yet.
    </p>
  );
}

function DiscoverCardLink({ event, children }) {
  if (event.linkTo) {
    return (
      <Link to={event.linkTo} state={event.linkState}>
        {children}
      </Link>
    );
  }
  return <Link to="/tickets">{children}</Link>;
}

export default function MarketplaceNewUISectionDiscover() {
  const [zipCode, setZipCode] = useState("");
  const [shareModal, setShareModal] = useState({ show: false, item: null });

  const featuredRaw = useSelector((state) => state.Admin.featuredEvents);
  const isLoading = useSelector((state) => state.Admin.adminloading);

  const latestFeaturedForHot = useMemo(
    () => selectLatestFeaturedForHot(featuredRaw),
    [featuredRaw]
  );
  const trendingFiltered = useMemo(() => selectTrendingFeatured(featuredRaw), [featuredRaw]);
  const communityFiltered = useMemo(() => selectCommunityItems(featuredRaw), [featuredRaw]);

  const hotDisplayBase = useMemo(() => {
    const sorted = [...latestFeaturedForHot].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
    return sorted
      .slice(0, 8)
      .map(mapFeaturedWrapperToHotCard)
      .filter(Boolean);
  }, [latestFeaturedForHot]);

  const trendingDisplayBase = useMemo(() => {
    return trendingFiltered
      .slice(0, 18)
      .map(mapFeaturedWrapperToTrendingCard)
      .filter(Boolean);
  }, [trendingFiltered]);

  const listingPrices = useDiscoverListingPrices(hotDisplayBase, trendingDisplayBase);

  const hotDisplay = useMemo(
    () => applyListingPrices(hotDisplayBase, listingPrices),
    [hotDisplayBase, listingPrices]
  );

  const trendingDisplay = useMemo(
    () => applyListingPrices(trendingDisplayBase, listingPrices),
    [trendingDisplayBase, listingPrices]
  );

  const communityDisplay = useMemo(() => {
    return communityFiltered
      .slice(0, 12)
      .map(mapCommunityWrapperToCard)
      .filter(Boolean);
  }, [communityFiltered]);

  return (
    <section className="bg-black py-20 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 mb-12">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="flex items-center gap-2 px-4">
            <TbTrendingUp className="w-4 h-4 text-[#E52324]" />
            <span className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-black">{"What's Trending"}</span>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {!isLoading && trendingDisplay.length === 0 ? (
            <DiscoverEmpty label="trending events" />
          ) : null}
          {trendingDisplay.map((event, idx) => (
            <DiscoverCardLink key={event.id} event={event}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
                className="group bg-[#111] rounded-2xl overflow-hidden border border-white/10 shadow-sm hover:shadow-xl hover:shadow-white/5 transition-all duration-300 cursor-pointer"
                whileHover={{ y: -4 }}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title || ""}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {event.badge ? (
                    <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider text-white ${event.badgeColor}`}>
                      {event.badge}
                    </span>
                  ) : null}
                </div>
                <div className="p-4">
                  <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1">{event.date}</div>
                  <h3 className="text-white font-black uppercase text-sm tracking-tight mb-1 group-hover:text-[#E52324] transition-colors">{event.title}</h3>
                  <div className="flex items-center gap-1 text-white/40 text-[11px] mb-3">
                    <TbMapPin className="w-3 h-3 shrink-0" />
                    <span className="truncate">{event.venue}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white text-xl font-black">${event.price}</span>
                    <span className="px-4 py-1.5 rounded-xl bg-[#E52324] text-white text-xs font-black uppercase tracking-wider hover:bg-[#c91f20] transition-colors">
                      Get Tickets
                    </span>
                  </div>
                </div>
              </motion.div>
            </DiscoverCardLink>
          ))}
        </div>

        <div className="flex items-center justify-between mb-10">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-black uppercase text-white tracking-tight flex items-center gap-3"
          >
            <TbFlame className="w-6 h-6 text-[#E52324]" /> Hot Events Near You
          </motion.h2>

          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white/[0.06] border border-white/[0.10] rounded-xl overflow-hidden">
              <div className="flex items-center gap-1.5 pl-3 pr-1">
                <TbMapPin className="w-3.5 h-3.5 text-[#E52324]" strokeWidth={2} />
              </div>
              <input
                type="text"
                inputMode="numeric"
                maxLength={5}
                placeholder="Zip Code"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                className="bg-transparent text-[13px] text-white placeholder:text-white/30 outline-none w-[72px] py-2 pr-1"
                style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}
              />
              {zipCode ? (
                <button type="button" onClick={() => setZipCode("")} className="pr-2.5 text-white/30 hover:text-white/60 transition-colors" aria-label="Clear zip">
                  <TbSearch className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              ) : null}
            </div>
            <Link to="/tickets" className="text-[#E52324] text-sm font-black uppercase tracking-wider hover:underline">
              See all →
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {!isLoading && hotDisplay.length === 0 ? (
            <DiscoverEmpty label="hot events" />
          ) : null}
          {hotDisplay.map((event, idx) => (
            <DiscoverCardLink key={event.id} event={event}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.04 }}
                className="relative rounded-2xl overflow-hidden cursor-pointer group bg-[#111] border border-white/[0.08] hover:border-white/15 transition-all duration-300"
                whileHover={{ y: -4 }}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title || ""}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  {event.badge ? (
                    <span className={`absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider text-white ${event.badgeColor} shadow-lg`}>
                      {event.badge}
                    </span>
                  ) : null}
                  <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider text-white/80 bg-black/40 backdrop-blur-sm border border-white/10">
                    {event.category}
                  </span>
                  <div className="absolute bottom-2.5 left-2.5">
                    <span className="text-white text-[18px] sm:text-[22px] font-black drop-shadow-lg">${event.price}</span>
                    <span className="text-white/50 text-[9px] ml-1">+</span>
                  </div>
                </div>
                <div className="p-3 sm:p-3.5">
                  <h3 className="text-white text-[12px] sm:text-[13px] font-black uppercase leading-tight tracking-tight mb-1.5 line-clamp-1 group-hover:text-[#E52324] transition-colors">
                    {event.title}
                  </h3>
                  <div className="flex items-center gap-1 text-white/35 text-[10px] mb-1">
                    <TbCalendarEvent className="w-3 h-3 shrink-0 text-white/25" />
                    <span>{event.date}</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/35 text-[10px]">
                    <TbMapPin className="w-3 h-3 shrink-0 text-white/25" />
                    <span className="truncate">{event.venue}</span>
                  </div>
                </div>
              </motion.div>
            </DiscoverCardLink>
          ))}
        </div>

        {/* Top Artists — hidden for now
        <MarketplaceNewUIArtistSpotlight />
        */}

        {/* Popular Teams & Leagues — hidden for now
        <div className="flex items-center gap-4 mb-10">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="flex items-center gap-2 px-4">
            <TbTrophy className="w-4 h-4 text-[#E52324]" />
            <span className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-black">Top Teams</span>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl sm:text-2xl font-black uppercase text-white tracking-tight flex items-center gap-3"
          >
            <TbTrophy className="w-5 h-5 text-[#E52324]" /> Popular Teams & Leagues
          </motion.h2>
          <Link to="/tickets" className="text-[#E52324] text-sm font-black uppercase tracking-wider hover:underline">
            See all →
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {topTeams.map((team, idx) => (
            <Link key={team.id} to="/tickets">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="group bg-[#111] rounded-2xl overflow-hidden border border-white/[0.08] hover:border-white/15 transition-all duration-300 cursor-pointer flex"
                whileHover={{ y: -4 }}
              >
                <div className="relative w-[140px] sm:w-[160px] shrink-0 overflow-hidden">
                  <img
                    src={team.image}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#111]/30" />
                </div>
                <div className="flex-1 p-4 flex flex-col justify-center">
                  <span className="inline-block px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider text-white/80 bg-white/10 border border-white/10 w-fit mb-2">{team.league}</span>
                  <h3 className="text-white text-[13px] sm:text-[14px] font-black uppercase tracking-tight mb-1.5 group-hover:text-[#E52324] transition-colors line-clamp-1">{team.name}</h3>
                  <div className="flex items-center gap-1 text-white/35 text-[10px] mb-2">
                    <TbCalendarEvent className="w-3 h-3 shrink-0 text-white/25" />
                    <span>Next: {team.nextGame}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/50 text-[11px]">
                      From <span className="text-white font-black text-[15px]">${team.price}</span>
                    </span>
                    <span className="px-3 py-1 rounded-lg bg-[#E52324] text-white text-[9px] font-black uppercase tracking-wider hover:bg-[#c91f20] transition-colors">Tickets</span>
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        */}

        <div className="flex items-center gap-4 mt-12 sm:mt-16 mb-10">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="flex items-center gap-2 px-4">
            <TbHeart className="w-4 h-4 text-[#E52324]" />
            <span className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-black">Community Events</span>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl sm:text-2xl font-black uppercase text-white tracking-tight flex items-center gap-3"
          >
            <TbHeart className="w-5 h-5 text-[#E52324]" /> Community & Local Events
          </motion.h2>
          <Link to="/tickets" className="text-[#E52324] text-sm font-black uppercase tracking-wider hover:underline">
            See all →
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {!isLoading && communityDisplay.length === 0 ? (
            <DiscoverEmpty label="community events" />
          ) : null}
          {communityDisplay.map((event, idx) => (
            <DiscoverCardLink key={event.id} event={event}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="group bg-[#111] rounded-2xl overflow-hidden border border-white/[0.08] hover:border-white/15 transition-all duration-300 cursor-pointer"
                whileHover={{ y: -4 }}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title || ""}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  {event.badge ? (
                    <span className={`absolute top-2 left-2 px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider text-white ${event.badgeColor} shadow-lg`}>
                      {event.badge}
                    </span>
                  ) : null}
                  {event.shareItem ? (
                    <button
                      type="button"
                      aria-label="Share event"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShareModal({ show: true, item: event.shareItem });
                      }}
                      className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white/90 hover:text-white hover:bg-black/75 transition-colors"
                    >
                      <FaShareAlt className="w-3 h-3" />
                    </button>
                  ) : null}
                  <div className="absolute bottom-2 left-2">
                    <span className="text-white text-[16px] font-black drop-shadow-lg">{event.price === 0 ? "Free" : `$${event.price}`}</span>
                  </div>
                </div>
                <div className="p-2.5">
                  <h3 className="text-white text-[11px] font-black uppercase leading-tight tracking-tight mb-1 line-clamp-1 group-hover:text-[#E52324] transition-colors">{event.title}</h3>
                  <div className="flex items-center gap-1 text-white/35 text-[9px] mb-0.5">
                    <TbCalendarEvent className="w-2.5 h-2.5 shrink-0 text-white/25" />
                    <span>{event.date}</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/35 text-[9px]">
                    <TbMapPin className="w-2.5 h-2.5 shrink-0 text-white/25" />
                    <span className="truncate">{event.venue}</span>
                  </div>
                </div>
              </motion.div>
            </DiscoverCardLink>
          ))}
        </div>
      </div>

      {shareModal.show && shareModal.item ? (
        <ShareModal
          setShow={() => setShareModal({ show: false, item: null })}
          item={shareModal.item}
          Share={shareModal.show}
        />
      ) : null}
    </section>
  );
}
